import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Building2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { PublicLayout } from "@/components/public/PublicLayout";
import { HeroSection } from "@/components/public/HeroSection";
import { BeforeAfterSection } from "@/components/public/BeforeAfterSection";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
interface FeaturedMemorial {
  id: string;
  first_name: string;
  last_name: string | null;
  birth_date: string | null;
  death_date: string | null;
  profile_picture_url: string | null;
}

const PublicLanding = () => {
  const navigate = useNavigate();
  const [featuredMemorials, setFeaturedMemorials] = useState<FeaturedMemorial[]>([]);
  const [isLoadingMemorials, setIsLoadingMemorials] = useState(true);
  
  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchFeaturedMemorials = async () => {
      try {
        const { data, error } = await supabase
          .from('featured_memorials')
          .select(`
            memorial_id,
            display_order,
            memorial:memorials_public(
              id, first_name, last_name, birth_date, death_date, profile_picture_url
            )
          `)
          .order('display_order', { ascending: true })
          .limit(6);

        if (error) throw error;

        const memorials = data
          ?.map(item => item.memorial)
          .filter((m): m is FeaturedMemorial => m !== null) || [];
        
        setFeaturedMemorials(memorials);
      } catch (error) {
        console.error('Error fetching featured memorials:', error);
      } finally {
        setIsLoadingMemorials(false);
      }
    };

    fetchFeaturedMemorials();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('consumer_inquiries')
        .insert({
          name: name.trim(),
          email: email.trim(),
          city: city.trim() || null,
          state: state.trim() || null,
          inquiry_type: 'consumer',
        });

      if (error) throw error;

      setSubmitted(true);
      setName("");
      setEmail("");
      setCity("");
      setState("");
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <HeroSection />

      <BeforeAfterSection />

      {/* The Product — medallion + memorial specs */}
      <section className="py-12 md:py-24 bg-brand-background relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: "url('/assets/granite-texture.webp')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6">
          {/* Medallion image */}
          <div className="flex justify-center mb-8 md:mb-10">
            <div className="relative">
              <div className="absolute inset-0 bg-brand-accent/10 rounded-full blur-3xl scale-75" />
              <img
                src="/assets/medallion.png"
                alt="The Pontis QR medallion"
                loading="lazy"
                className="relative w-40 md:w-64 drop-shadow-xl"
              />
            </div>
          </div>

          {/* Section header */}
          <div className="text-center mb-10 md:mb-14">
            <span className="text-sm tracking-wide uppercase text-brand-secondary">
              The Product
            </span>
            <h2 className="font-serif text-2xl md:text-4xl font-semibold text-brand-text-primary mt-2">
              Built to last. Designed to be remembered.
            </h2>
          </div>

          {/* Two spec cards */}
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {/* The Medallion */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgba(61,53,48,0.06)]">
              <h3 className="font-serif text-xl font-semibold text-brand-text-primary mb-5">
                The Medallion
              </h3>
              <ul className="space-y-3.5">
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  38mm diameter, under 1 oz
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Weatherproof stainless steel, rated for 100+ years outdoors
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Laser-etched QR code — won't fade, peel, or wash off
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Scan from feet away with any phone camera
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  No app, no battery, no maintenance — ever
                </li>
              </ul>
            </div>

            {/* The Memorial */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_20px_rgba(61,53,48,0.06)]">
              <h3 className="font-serif text-xl font-semibold text-brand-text-primary mb-5">
                The Memorial
              </h3>
              <ul className="space-y-3.5">
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Up to 1 GB of storage — roughly 10,000 photos or 500+ stories
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Memorial page loads in under 2 seconds
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  99.9% uptime — your memories are always accessible
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Unlimited contributors — anyone in the family can add memories
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Designed for ages 8 to 80+ — tap-friendly, no tech skills needed
                </li>
                <li className="flex items-start gap-3 text-sm text-brand-secondary leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary/40 mt-[7px] flex-shrink-0" />
                  Your data is permanently hosted — it doesn't disappear
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Memorials */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-primary mb-3">
              Featured Memorials
            </h2>
            <p className="text-lg text-brand-secondary">
              Real stories, real families.
            </p>
          </div>

          {isLoadingMemorials ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
            </div>
          ) : featuredMemorials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredMemorials.slice(0, 3).map(memorial => (
                <FeaturedMemorialCard
                  key={memorial.id}
                  memorial={memorial}
                  onView={(id) => navigate(`/memorial/${id}`)}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-brand-secondary">
              Memorials coming soon.
            </p>
          )}
        </div>
      </section>

      {/* Want Pontis Section - Softer approach */}
      <section className="py-12 md:py-16 bg-brand-primary text-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">
              Want Pontis for Your Family?
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Pontis medallions are installed by monument companies. Here's how you can bring 
              a digital memorial to your loved one.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Option 1: Ask your provider */}
            <div className="bg-white/10 rounded-xl p-6 text-center backdrop-blur-sm">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Ask Your Monument Provider</h3>
              <p className="text-white/80 mb-4">
                If you're working with a monument company, ask them about 
                offering Pontis QR medallions.
              </p>
              <p className="text-sm text-white/60 italic border-t border-white/20 pt-4 mt-4">
                "Do you offer Pontis digital memorials?"
              </p>
            </div>
            
            {/* Option 2: Consumer Interest Form */}
            <div className="bg-white rounded-xl p-6 text-brand-primary">
              <h3 className="text-xl font-semibold mb-3 text-center">
                Request Pontis in Your Area
              </h3>
              <p className="text-brand-secondary text-sm mb-4 text-center">
                Tell us where you are and we'll connect with providers near you.
              </p>
              
              {submitted ? (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <p className="text-green-600 font-medium">
                    Thank you! We'll be in touch soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <Input 
                    placeholder="Your Name" 
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-brand-background border-brand-primary/20"
                  />
                  <Input 
                    type="email" 
                    placeholder="Email Address" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-brand-background border-brand-primary/20"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      placeholder="City" 
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="bg-brand-background border-brand-primary/20"
                    />
                    <Input 
                      placeholder="State" 
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="bg-brand-background border-brand-primary/20"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-brand-accent hover:bg-brand-accent/90 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Request Information"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Consumer FAQ Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-brand-secondary">
              Everything you need to know about Pontis memorials.
            </p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="border-brand-primary/10">
              <AccordionTrigger className="text-left text-brand-primary hover:text-brand-accent">
                What is a Pontis memorial?
              </AccordionTrigger>
              <AccordionContent className="text-brand-secondary">
                A Pontis memorial is a QR code medallion installed on a headstone that, when scanned, 
                takes visitors to a beautiful digital memorial page. This page can include photos, 
                videos, stories, and tributes celebrating your loved one's life.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border-brand-primary/10">
              <AccordionTrigger className="text-left text-brand-primary hover:text-brand-accent">
                How do I visit a Pontis memorial?
              </AccordionTrigger>
              <AccordionContent className="text-brand-secondary">
                Simply open your smartphone's camera and point it at the QR code on the medallion. 
                A link will appear—tap it to view the memorial page. No app download required.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="border-brand-primary/10">
              <AccordionTrigger className="text-left text-brand-primary hover:text-brand-accent">
                Can I create a memorial for my loved one?
              </AccordionTrigger>
              <AccordionContent className="text-brand-secondary">
                Pontis medallions are installed by our partner monument companies. Ask your local 
                monument provider if they offer Pontis, or fill out our interest form and we'll 
                connect you with a partner in your area.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="border-brand-primary/10">
              <AccordionTrigger className="text-left text-brand-primary hover:text-brand-accent">
                Is my information private?
              </AccordionTrigger>
              <AccordionContent className="text-brand-secondary">
                Yes. Memorial pages are only accessible to those who scan the QR code or have the 
                direct link. You control what content is shared, and families can manage privacy 
                settings through their memorial dashboard.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="border-brand-primary/10">
              <AccordionTrigger className="text-left text-brand-primary hover:text-brand-accent">
                How do I leave a tribute?
              </AccordionTrigger>
              <AccordionContent className="text-brand-secondary">
                When viewing a memorial, look for the "Leave a Tribute" option. You can share a 
                written message, memory, or condolence that will be visible to the family and 
                other visitors.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer CTA - Gentle close */}
      <section className="py-12 md:py-16 bg-brand-background">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-primary mb-4">
            Preserving Memories for Generations
          </h2>
          <p className="text-lg text-brand-secondary mb-8">
            Every story deserves to be told. Every life deserves to be remembered.
          </p>
          
          <Link to="/search">
            <Button size="lg" className="bg-brand-primary text-white hover:bg-brand-primary/90">
              <Search className="mr-2 h-5 w-5" />
              Explore Memorials
            </Button>
          </Link>
          
          <p className="mt-8 text-sm text-brand-secondary">
            Are you a monument company?{" "}
            <Link to="/partners" className="text-brand-accent hover:underline font-medium">
              Partner with us →
            </Link>
          </p>
        </div>
      </section>
    </PublicLayout>
  );
};

// Sub-components
const FeaturedMemorialCard = ({ memorial, onView }: { memorial: FeaturedMemorial; onView: (id: string) => void }) => {
  const formatYear = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).getFullYear();
  };

  const birthYear = formatYear(memorial.birth_date);
  const deathYear = formatYear(memorial.death_date);
  const lifespan = birthYear && deathYear ? `${birthYear} - ${deathYear}` : birthYear ? `Born ${birthYear}` : null;

  return (
    <Card className="border border-brand-primary/20 rounded-2xl hover:shadow-[0_8px_30px_rgba(61,53,48,0.12)] transition-all duration-300">
      <CardContent className="p-6 flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full overflow-hidden mb-4">
          {memorial.profile_picture_url ? (
            <img
              src={memorial.profile_picture_url}
              alt={buildMemorialDisplayName(memorial)}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-brand-primary/10 flex items-center justify-center">
              <span className="text-brand-primary font-semibold text-xl">
                {memorial.first_name.charAt(0)}
              </span>
            </div>
          )}
        </div>

        <h3 className="font-serif text-lg font-semibold text-brand-text-primary">
          {buildMemorialDisplayName(memorial)}
        </h3>
        {lifespan && (
          <p className="text-sm text-brand-secondary mt-1">{lifespan}</p>
        )}

        <Button
          variant="outline"
          className="mt-4 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white"
          onClick={() => onView(memorial.id)}
        >
          View Memorial
        </Button>
      </CardContent>
    </Card>
  );
};

export default PublicLanding;